import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const POLL_INTERVAL_MS = 2000;

const normalizeHandoff = (data) => ({
  code: data.code,
  context: data.context,
  mobileUrl: data.mobile_url,
  statusUrl: data.status_url,
  expiresAt: data.expires_at,
});

const isExpired = (handoff) => {
  if (!handoff?.expiresAt) return false;
  const expiresAt = new Date(handoff.expiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
};

const useSelectionUploadHandoff = ({
  context,
  createPath,
  statusPath,
  selectionId,
  shopifySessionId,
  media,
  frameStyleColourId,
  frameSkuId,
  onComplete,
  onImagesReceived,
}) => {
  const [handoff, setHandoff] = useState(null);
  const [status, setStatus] = useState("idle");
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const seenImageIdsRef = useRef(new Set());
  const didCompleteRef = useRef(false);

  const pollStatus = useCallback(
    async (activeHandoff) => {
      const response = await axios.get(statusPath, {
        params: { code: activeHandoff.code },
      });

      const data = response.data;
      setStatus(data.status);
      setUploadedCount(data.uploaded_count || 0);

      if (Array.isArray(data.images) && data.images.length > 0) {
        const nextImages = data.images.filter((image) => {
          if (seenImageIdsRef.current.has(image.id)) return false;
          seenImageIdsRef.current.add(image.id);
          return true;
        });

        if (nextImages.length > 0) {
          setUploadedImages((prev) => [...prev, ...nextImages]);
          if (onImagesReceived) onImagesReceived(nextImages);
        }
      }

      if (
        data.status === "uploaded" &&
        data.next_url &&
        onComplete &&
        !didCompleteRef.current
      ) {
        didCompleteRef.current = true;
        onComplete(data.next_url);
      }
    },
    [onComplete, onImagesReceived, statusPath],
  );

  const startHandoff = useCallback(
    async ({ force = false } = {}) => {
      if (isCreating) return null;
      if (!force && handoff && !isExpired(handoff)) return handoff;

      setIsCreating(true);
      setError(null);
      setStatus("idle");
      setUploadedCount(0);
      setUploadedImages([]);
      seenImageIdsRef.current = new Set();
      didCompleteRef.current = false;

      try {
        const response = await axios.post(createPath, {
          selection_upload_handoff: {
            context,
            selection_id: selectionId,
            shopify_session_id: shopifySessionId,
            media,
            frame_style_colour_id: frameStyleColourId,
            frame_sku_id: frameSkuId,
          },
        });

        const nextHandoff = normalizeHandoff(response.data);
        setHandoff(nextHandoff);
        setStatus("pending");
        return nextHandoff;
      } catch (requestError) {
        console.error("Failed to create selection upload handoff:", requestError);
        setError("We couldn't generate a QR code just now. Please try again.");
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [
      context,
      createPath,
      frameSkuId,
      frameStyleColourId,
      handoff,
      isCreating,
      media,
      selectionId,
      shopifySessionId,
    ],
  );

  useEffect(() => {
    if (!handoff) return undefined;
    if (isExpired(handoff)) {
      setStatus("expired");
      return undefined;
    }

    let timeoutId;
    let cancelled = false;

    const tick = async () => {
      try {
        await pollStatus(handoff);
      } catch (pollError) {
        console.error("Failed to poll selection upload handoff:", pollError);
        if (!cancelled) {
          setError("We lost the connection to this upload session.");
        }
      }

      if (cancelled) return;

      if (isExpired(handoff)) {
        setStatus("expired");
        return;
      }

      timeoutId = window.setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [handoff, pollStatus]);

  return {
    handoff,
    status,
    uploadedCount,
    uploadedImages,
    isCreating,
    error,
    startHandoff,
    refreshHandoff: () => startHandoff({ force: true }),
  };
};

export default useSelectionUploadHandoff;
